import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "sonner";
import { useEffect, lazy, Suspense } from "react";
import { useLocation } from "react-router-dom";
import Layout from "@/components/Layout";

/* ── Lazy loading : chaque page chargée uniquement quand visitée ── */
const HomePage = lazy(() => import("@/pages/HomePage"));
const OrderPage = lazy(() => import("@/pages/OrderPage"));
const PaymentPage = lazy(() => import("@/pages/PaymentPage"));
const TrackingPage = lazy(() => import("@/pages/TrackingPage"));
const AdminLoginPage = lazy(() => import("@/pages/AdminLoginPage"));
const AdminDashboard = lazy(() => import("@/pages/AdminDashboard"));
const MentionsLegales = lazy(() => import("@/pages/MentionsLegales"));
const PolitiqueConfidentialite = lazy(() => import("@/pages/PolitiqueConfidentialite"));
const CGU = lazy(() => import("@/pages/CGU"));
const NotFoundPage = lazy(() => import("@/pages/NotFoundPage"));

/* ── Titres des pages ── */
const PAGE_TITLES = {
  "/": "GOFRET - Transport Express Colis & Marchandises partout en France",
  "/commander": "Commander un transport - GOFRET Express France",
  "/suivi": "Suivre ma commande - GOFRET Transport Express",
  "/mentions-legales": "Mentions légales - GOFRET Transport",
  "/politique-confidentialite": "Politique de confidentialité - GOFRET Transport",
  "/cgu": "Conditions générales d'utilisation - GOFRET Transport",
  "/admin-gofret": "Administration - GOFRET",
};

/* ── Scroll automatique en haut à chaque changement de page ── */
function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "instant"
    });
  }, [pathname]);

  return null;
}

/* ── Mise à jour du titre + tracking page vue ── */
function PageTitleUpdater() {
  const location = useLocation();

  useEffect(() => {
    const basePath = location.pathname.replace(/\/[^/]+$/, "") || location.pathname;

    let title = PAGE_TITLES[location.pathname] || PAGE_TITLES[basePath];

    if (!title) {
      if (location.pathname.startsWith("/paiement/")) {
        title = "Paiement sécurisé - GOFRET Transport Express";
      } else if (location.pathname.startsWith("/suivi/")) {
        title = "Suivi de commande - GOFRET Transport Express";
      } else {
        title = "GOFRET - Transport Express Colis & Marchandises partout en France";
      }
    }

    document.title = title;

    /* Google Ads : page vue */
    if (window.gtag) {
      window.gtag("event", "page_view", {
        page_path: location.pathname,
      });
    }

    /* Posthog : page vue */
    if (window.posthog) {
      window.posthog.capture("$pageview");
    }

  }, [location.pathname]);

  return null;
}

/* ── Fallback pendant le chargement d'une page ── */
function PageLoader() {
  return (
    <div
      style={{
        minHeight: "60vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <div
        style={{
          width: "32px",
          height: "32px",
          border: "3px solid #F4F4F2",
          borderTop: "3px solid #F4590A",
          borderRadius: "50%",
          animation: "spin 0.7s linear infinite",
        }}
      />
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}

function App() {
  return (
    <div className="font-body">
      <Toaster position="top-right" richColors />

      <BrowserRouter>

        <ScrollToTop />
        <PageTitleUpdater />

        <Suspense fallback={<PageLoader />}>
          <Routes>

            <Route path="/" element={<Layout><HomePage /></Layout>} />

            <Route path="/commander" element={<Layout><OrderPage /></Layout>} />

            <Route path="/paiement/:orderId" element={<Layout><PaymentPage /></Layout>} />

            <Route path="/suivi" element={<Layout><TrackingPage /></Layout>} />

            <Route path="/suivi/:orderNumber" element={<Layout><TrackingPage /></Layout>} />

            <Route path="/mentions-legales" element={<Layout><MentionsLegales /></Layout>} />

            <Route path="/politique-confidentialite" element={<Layout><PolitiqueConfidentialite /></Layout>} />

            <Route path="/cgu" element={<Layout><CGU /></Layout>} />

            <Route path="/admin-gofret" element={<AdminLoginPage />} />

            <Route path="/admin-gofret/dashboard" element={<AdminDashboard />} />

            <Route path="*" element={<Layout><NotFoundPage /></Layout>} />

          </Routes>
        </Suspense>

      </BrowserRouter>
    </div>
  );
}

export default App;
